import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useToast } from "./ui/ToastModal";
import { Modal } from "./ui/ToastModal";
import {
  Calendar,
  FileText,
  Upload,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Calculator,
  Download,
  Layers,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Wrench,
} from "lucide-react";
import type { ClaimsProject, ClaimsVariation } from "../types/database.types";
import { useAiModel } from "../hooks/useAiModel";
import { useSubscription } from "../hooks/useSubscription";
import { loadHireClaimedElsewhere, periodStamp, type PriorHireData } from "../lib/hireClaimedLoad";
import { retentionCeilingAmount, formatRetentionCeilingPercent } from "../lib/retentionCeiling";
import { computeClaimSummary, isPriorClaimPeriod, retentionCeilingStatus, computeHireClaimedToDate } from "../lib/claimSummary";
import {
  sumAccumulatedRetention,
  sumPriorRetentionOnPaidClaims,
} from "../lib/retentionCarryForward";
import {
  calculatePaidVariationsTotal,
  calculateVariationClaimContribution,
  calculateVariationClaimPreview,
} from "../lib/variationClaimPreview";
import {
  hireTupleKey,
  isHireRowClaimable,
  isHireRowReclaimable,
  reassignUnselectedReclaims,
} from "../lib/hireClaimability";
import {
  calculateHireBillingPeriod,
  embeddedVariationDiscountPercent,
} from "../lib/hireBillingPeriod";
import { excludeHireRowsWithRemovedParent } from "../lib/dashpivotRemoval";
import {
  DEFAULT_CLAIM_DAY,
  DEFAULT_PAYMENT_PERIOD_DAYS,
  daysBetweenDates,
  deriveClaimDate,
  deriveDueDate,
  resolvePaymentPeriodDays,
  resolveProjectTiming,
  type ProjectTimingDefaults,
} from "../lib/claimTiming";
import { claimTimingDb } from "../lib/claimTimingDb";
import { PDFDocument } from "pdf-lib";
import { logError, logWarn } from "../lib/logError";

interface UploadedFile {
  name: string;
  type: string;
  base64: string;
  size: number;
}

interface ProgressClaimProps {
  isOpen: boolean;
  onClose: () => void;
  project: ClaimsProject;
  variations: ClaimsVariation[];
  onReportGenerated?: () => void;
}

interface ClaimCategoryQueryRow {
  id?: string;
  custom_amount?: number | null;
  is_custom_amount_only?: boolean | null;
  claims_subcategories?: Array<{
    id: string;
    amount?: number | null;
  }> | null;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type RetentionReleaseMode = "amount" | "percent";

// SEQ-218: an unpaid month an earlier claim already claimed, offered for re-claim.
interface ReclaimHireRow {
  row: HireBillingPreviewRow;
  fromReportId: string;
  /** The claim number of the earlier claim, or null if it has none. */
  fromClaimNumber: number | null;
  /** True when this claim (being regenerated) already re-claimed the row. */
  wasReclaimedHere: boolean;
}

interface HireBillingPreviewRow {
  id: string;
  // Amendment 4: stable identity used for the generate payload. The row id
  // (above) churns when sync-variations recreates the billing row; this
  // register id (NOT NULL in the schema) plus billing month/year survives, so
  // selection is resolved against current truth server-side.
  hireRegisterId: string;
  subDocketNo: string;
  sourceDocketNo: string;
  item: string;
  billingMonth: number;
  billingYear: number;
  periodStartDate: string | null;
  periodEndDate: string | null;
  periodLabel: string;
  weeksInPeriod: number;
  storedWeeksInPeriod: number;
  amount: number;
  storedAmount: number;
  status: string;
  includedInClaim: boolean;
  claimReportId: string | null;
  paid: boolean;
  paidAmount: number;
  isOutstanding: boolean;
  canSelect: boolean;
  defaultSelected: boolean;
  wasRecalculated: boolean;
}


export function ProgressClaimModal({
  isOpen,
  onClose,
  project,
  variations,
  onReportGenerated,
}: ProgressClaimProps) {
  const { addToast } = useToast();
  const aiModel = useAiModel();
  const { logAiUsage } = useSubscription();

  // State
  const now = new Date();
  const [step, setStep] = useState<"input" | "generating" | "review">("input");
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [submissionDate, setSubmissionDate] = useState("");
  const [paymentDueDate, setPaymentDueDate] = useState("");
  // SEQ-169: the payment period this claim uses, seeded from the project config
  // and editable here. Held as a string because it is bound to a number input.
  const [paymentPeriodDays, setPaymentPeriodDays] = useState(
    DEFAULT_PAYMENT_PERIOD_DAYS.toString(),
  );
  // The project defaults in force, snapshotted onto the report at generation so
  // a later settings change cannot rewrite whether this claim was an override.
  const [timingDefaults, setTimingDefaults] = useState<ProjectTimingDefaults>({
    claimDay: DEFAULT_CLAIM_DAY,
    paymentPeriodDays: DEFAULT_PAYMENT_PERIOD_DAYS,
  });
  // Once the operator edits the due date directly, the claim date stops moving
  // it. Same smart lock idiom as the retention fields in ClaimEditModal.
  const dueDateEditedRef = useRef(false);
  const [retentionPct, setRetentionPct] = useState(10);
  const [retentionReleaseMode, setRetentionReleaseMode] =
    useState<RetentionReleaseMode>("amount");
  const [retentionReleaseAmount, setRetentionReleaseAmount] = useState("");
  const [retentionReleasePercent, setRetentionReleasePercent] = useState("");
  const [notes, setNotes] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [statutoryFile, setStatutoryFile] = useState<UploadedFile | null>(null);
  // Selected variations (all selected by default)
  const [selectedVariationIds, setSelectedVariationIds] = useState<Set<string>>(
    new Set(),
  );
  const [includeVariations, setIncludeVariations] = useState(true);

  // Review Step State
  const [generatedData, setGeneratedData] = useState<{
    pdfBase64: string;
    filename: string;
    emailDraft?: { subject: string; body: string };
    aiGenerated?: boolean;
    variationsPdfUrls?: (string | null)[];
    statutoryDeclarationData?: { name: string; base64: string; type: string } | null;
  } | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [aiGenerated, setAiGenerated] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [mergeState, setMergeState] = useState<'idle' | 'merging' | 'done' | 'error'>('idle');
  const [mergeProgress, setMergeProgress] = useState({ current: 0, total: 0 });
  const [showIndividualDownloads, setShowIndividualDownloads] = useState(false);
  const [showStatutoryWarning, setShowStatutoryWarning] = useState(false);

  // Retention ceiling state
  const [retentionCeiling, setRetentionCeiling] = useState(0);
  const [accumulatedRetention, setAccumulatedRetention] = useState(0);
  const [retentionCeilingReached, setRetentionCeilingReached] = useState(false);
  const [remainingRetention, setRemainingRetention] = useState(0);
  const [availableRetentionRelease, setAvailableRetentionRelease] = useState(0);
  const [existingReport, setExistingReport] = useState<{
    id: string;
    submission_date: string | null;
    payment_due_date: string | null;
  } | null>(null);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);

  const [loadingSettings, setLoadingSettings] = useState(true);

  // Financial preview state
  const [previewContractValue, setPreviewContractValue] = useState(0);
  const [previewVarsThisMonth, setPreviewVarsThisMonth] = useState(0);
  const [previewVarsOutstanding, setPreviewVarsOutstanding] = useState(0);
  // SEQ-145 new-format headline inputs (A = CumP + PaidVar + V + hire, B = paid/1.1 + prior held retention).
  const [previewCumulativeProgress, setPreviewCumulativeProgress] = useState(0);
  const [previewPaidByBuilderIncGst, setPreviewPaidByBuilderIncGst] = useState(0);
  const [previewPriorRetentionOnPaidClaims, setPreviewPriorRetentionOnPaidClaims] =
    useState(0);
  // SEQ-216: rows claimed elsewhere (earlier reports, paid rows of this report,
  // paid unattached rows), loaded on their own so a failure is visible and a
  // stale response from a previous month cannot land after a newer one.
  const [priorHireState, setPriorHireState] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [priorHireData, setPriorHireData] = useState<
    (PriorHireData & { stamp: string }) | null
  >(null);
  const [priorHireRetry, setPriorHireRetry] = useState(0);
  // True while loadDefaults (retention, receipts) re-runs for a new month.
  const [reloadingDefaults, setReloadingDefaults] = useState(false);
  // A failed query that feeds A, B or C must not read as a ready preview of zeros.
  const [defaultsError, setDefaultsError] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  // Stamp of the period (and existing report) the preview rows were computed
  // for, so one frame of the previous month's rows can never read as ready.
  const [previewStamp, setPreviewStamp] = useState("");
  const [previewHireRows, setPreviewHireRows] = useState<HireBillingPreviewRow[]>(
    [],
  );
  const [previewHireSourceVariationIds, setPreviewHireSourceVariationIds] =
    useState<Set<string>>(new Set());
  const [selectedHireBillingIds, setSelectedHireBillingIds] = useState<Set<string>>(
    new Set(),
  );
  const hireSelectionContextRef = useRef("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  // SEQ-218: unpaid months an EARLIER claim already claimed. Offered in their
  // own group, unticked by default, and sent in their own request field so the
  // normal selection is untouched. `reclaimTouched` holds only what the user
  // changed; a row this claim already re-claimed (regeneration) starts ticked.
  const [reclaimTouched, setReclaimTouched] = useState<Map<string, boolean>>(
    new Map(),
  );
  useEffect(() => {
    setReclaimTouched(new Map());
  }, [isOpen, project.id, selectedYear, selectedMonth, existingReport?.id]);

  // Below this width the re-claim rows stack the badge under the item line, so
  // the price always stays on screen.
  const [narrowViewport, setNarrowViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 480px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(max-width: 480px)");
    const update = () => setNarrowViewport(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const reclaimRows = useMemo<ReclaimHireRow[]>(() => {
    if (!priorHireData) return [];
    const out: ReclaimHireRow[] = [];
    for (const row of previewHireRows) {
      if (row.paid || !(row.storedAmount > 0)) continue;
      const frozenFrom =
        existingReport?.id && row.claimReportId === existingReport.id
          ? priorHireData.reclaimOrigins.get(
              hireTupleKey(row.hireRegisterId, row.billingMonth, row.billingYear),
            )
          : undefined;
      const fromReportId = frozenFrom
        ? frozenFrom
        : isHireRowReclaimable(
              {
                paid: row.paid,
                amount: row.storedAmount,
                claim_report_id: row.claimReportId,
              },
              selectedYear,
              selectedMonth,
              priorHireData.reports,
            )
          ? row.claimReportId
          : null;
      if (!fromReportId) continue;
      const fromReport = priorHireData.reports.find((r) => r.id === fromReportId);
      out.push({
        row,
        fromReportId,
        fromClaimNumber:
          Number(fromReport?.claim_number) > 0
            ? Number(fromReport?.claim_number)
            : null,
        wasReclaimedHere: Boolean(frozenFrom),
      });
    }
    return out;
  }, [priorHireData, previewHireRows, existingReport?.id, selectedYear, selectedMonth]);

  const reclaimIds = useMemo(
    () => new Set(reclaimRows.map((r) => r.row.id)),
    [reclaimRows],
  );
  const selectedReclaimRows = useMemo(
    () =>
      reclaimRows.filter((r) =>
        reclaimTouched.has(r.row.id)
          ? reclaimTouched.get(r.row.id)
          : r.wasReclaimedHere,
      ),
    [reclaimRows, reclaimTouched],
  );

  // The normal list: every row that is not offered as a re-claim.
  const mainHireRows = useMemo(
    () => previewHireRows.filter((row) => !reclaimIds.has(row.id)),
    [previewHireRows, reclaimIds],
  );
  const selectedHireRows = useMemo(
    () =>
      previewHireRows.filter(
        (row) => selectedHireBillingIds.has(row.id) && !reclaimIds.has(row.id),
      ),
    [previewHireRows, selectedHireBillingIds, reclaimIds],
  );

  // New hire this claim (counts toward the claim subtotal) and the re-claimed
  // months (shown in "Install & Hire this claim" but not new revenue). A
  // re-claim keeps the amount already claimed, never the re-priced one.
  const previewInstallHire = useMemo(
    () =>
      selectedHireRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [selectedHireRows],
  );
  const previewReclaimedHire = useMemo(
    () =>
      selectedReclaimRows.reduce(
        (sum, r) => sum + Number(r.row.storedAmount || 0),
        0,
      ),
    [selectedReclaimRows],
  );

  // SEQ-216: A's hire = ticked rows + hire claimed elsewhere, the same function
  // the edge function runs. A row that is ticked can never also count as prior.
  const previewHireClaimed = useMemo(
    () =>
      priorHireData
        ? computeHireClaimedToDate({
            selectedRows: [
              ...selectedHireRows,
              ...selectedReclaimRows.map((r) => ({
                id: r.row.id,
                amount: r.row.storedAmount,
              })),
            ],
            reports: priorHireData.reports,
            // An unticked row this claim already re-claimed goes back to its
            // original claim on save, so for A it is still claimed there.
            rows: reassignUnselectedReclaims(
              priorHireData.rows,
              new Set([
                ...selectedHireRows.map((row) => row.id),
                ...selectedReclaimRows.map((r) => r.row.id),
              ]),
              priorHireData.reclaimOrigins,
              priorHireData.currentReportId,
            ),
            claimYear: selectedYear,
            claimMonth: selectedMonth,
            currentReportId: priorHireData.currentReportId,
          })
        : null,
    [priorHireData, selectedHireRows, selectedReclaimRows, selectedYear, selectedMonth],
  );
  const expectedStamp = periodStamp(selectedYear, selectedMonth, existingReport?.id);
  const previewFresh =
    previewStamp === expectedStamp && priorHireData?.stamp === expectedStamp;
  const previewFailed =
    defaultsError || previewError || priorHireState === "error";
  const previewReady =
    !loadingPreview &&
    !reloadingDefaults &&
    !previewFailed &&
    priorHireState === "ready" &&
    previewFresh;

  // PaidVar for the new-format headline (A includes already-paid variations).
  // Exact mirror of the edge function's paid set: reconciliation status paid,
  // fully certified, never invalid, hire-stripped and credit-signed.
  const previewPaidVariationsTotal = useMemo(
    () =>
      calculatePaidVariationsTotal(variations, previewHireSourceVariationIds),
    [variations, previewHireSourceVariationIds],
  );

  // Load default settings + retention ceiling
  useEffect(() => {
    let cancelled = false;
    const loadDefaults = async () => {
      setReloadingDefaults(true);
      setDefaultsError(false);
      try {
        // Fetch settings and retention data in parallel
        const [settingsRes, reportsRes, categoriesRes, existingRes, configRes] =
          await Promise.all([
            supabase
              .from("claims_settings")
              .select("setting_key, setting_value")
              .in("setting_key", [
                "default_retention_percentage",
                "default_claim_submission_day",
              ]),
            supabase
              .from("claims_reports")
              .select(
                "id, retention_amount, retention_received_amount, paid_by_builder, claim_month, claim_year, reconciliation_summary",
              )
              .eq("project_id", project.id),
            supabase
              .from("claims_categories")
              .select("*, claims_subcategories(*)")
              .eq("project_id", project.id),
            supabase
              .from("claims_reports")
              .select("id, submission_date, payment_due_date")
              .eq("project_id", project.id)
              .eq("claim_month", selectedMonth)
              .eq("claim_year", selectedYear)
              .maybeSingle(),
            // SEQ-169 project claim timing. Selected on its own so that a
            // missing table (the build shipping ahead of the migration) fails
            // this one query and falls back to the organisation setting,
            // instead of breaking the rest of the modal.
            claimTimingDb
              .from("claims_project_config")
              .select("default_claim_day, payment_period_days")
              .eq("project_id", project.id)
              .maybeSingle(),
          ]);
        // A newer month/year selection owns the state now.
        if (cancelled) return;
        // These feed B, the retention ceiling and the existing-report id.
        if (reportsRes.error) throw reportsRes.error;
        if (categoriesRes.error) throw categoriesRes.error;
        if (existingRes.error) throw existingRes.error;

        // Existing report check for override
        setExistingReport(existingRes.data || null);

        // Compute original contract sum from categories (custom_amount OR subcategories, NOT both)
        let originalContractSum = 0;
        const categoryRows = (categoriesRes.data || []) as ClaimCategoryQueryRow[];

        categoryRows.forEach((cat) => {
          if (cat.is_custom_amount_only) {
            // Custom categories: use only custom_amount, skip subcategories
            originalContractSum += Number(cat.custom_amount || 0);
          } else {
            // Normal categories: sum subcategories
            const subs = cat.claims_subcategories || [];
            subs.forEach((sub) => {
              originalContractSum += Number(sub.amount || 0);
            });
          }
        });

        // Retention ceiling = the project's percentage (default 5) of the retention base
        // Mode: original (default), revised (original + approved vars), custom (locked-in amount)
        const mode = project.retention_ceiling_mode || (project.retention_on_revised ? 'revised' : 'original');
        let retentionBase = originalContractSum;
        if (mode === 'revised') {
          // The same certified figure the claim headline and the generated PDF
          // use. It used to sum approved_amount raw, which ADDED a credit to
          // the contract instead of reducing it and counted invalid
          // variations, so this screen's ceiling could differ from the project
          // page's (ProjectDetail has always subtracted credits).
          const approvedVarTotal = calculatePaidVariationsTotal(
            variations,
            previewHireSourceVariationIds,
          );
          retentionBase = originalContractSum + approvedVarTotal;
        } else if (mode === 'custom' && project.retention_custom_amount) {
          retentionBase = Number(project.retention_custom_amount);
        }
        const ceiling = retentionCeilingAmount(retentionBase, project.retention_ceiling_percent);
        setRetentionCeiling(ceiling);

        // Accumulated retention (excluding current month if re-generating)
        const accumulated = sumAccumulatedRetention(
          reportsRes.data || [],
          selectedYear,
          selectedMonth,
        );
        setAccumulatedRetention(accumulated);

        const receivedRetention = (reportsRes.data || [])
          .reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            (sum: number, r: any) => sum + Number(r.retention_received_amount || 0),
            0,
          );
        setAvailableRetentionRelease(Math.max(0, accumulated - receivedRetention));

        const remaining = Math.max(0, ceiling - accumulated);
        setRemainingRetention(remaining);
        const ceilingHit = remaining <= 0;
        setRetentionCeilingReached(ceilingHit);

        // SEQ-145 B components: paid receipts (inc GST) and prior retention held on
        // PAID claims only, from periods STRICTLY BEFORE the claim being generated
        // (audit fix: mirrors the edge function's isPriorClaimPeriod filter so a
        // back-dated preview cannot absorb later claims' receipts/retention).
        const priorReports = (reportsRes.data || []).filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          (r: any) =>
            isPriorClaimPeriod(
              Number(r.claim_year),
              Number(r.claim_month),
              selectedYear,
              selectedMonth,
            ),
        );
        const paidByBuilderIncGst = priorReports.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          (sum: number, r: any) => sum + Number(r.paid_by_builder || 0),
          0,
        );
        setPreviewPaidByBuilderIncGst(paidByBuilderIncGst);
        const priorRetentionOnPaidClaims = sumPriorRetentionOnPaidClaims(
          reportsRes.data || [],
          selectedYear,
          selectedMonth,
        );
        setPreviewPriorRetentionOnPaidClaims(priorRetentionOnPaidClaims);

        // Settings
        const defaults: Record<string, string> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        (settingsRes.data || []).forEach((item: any) => {
          defaults[item.setting_key] = item.setting_value || "";
        });

        if (ceilingHit) {
          setRetentionPct(0);
        } else if (defaults.default_retention_percentage) {
          setRetentionPct(parseFloat(defaults.default_retention_percentage));
        }

        // SEQ-169 claim timing. Precedence: the project config row, then the
        // organisation setting, then the constants that match today's
        // behaviour, so this works whether or not the migration has landed.
        const timing = resolveProjectTiming({
          configClaimDay: configRes.error
            ? null
            : configRes.data?.default_claim_day ?? null,
          configPaymentPeriodDays: configRes.error
            ? null
            : configRes.data?.payment_period_days ?? null,
          organizationClaimDay: defaults.default_claim_submission_day,
        });
        setTimingDefaults(timing);
        dueDateEditedRef.current = false;

        const stored = existingRes.data;
        if (stored?.submission_date && stored?.payment_due_date) {
          // Regenerating a claim that already exists. Its dates are a stored
          // fact, so they load as they were issued rather than being recomputed
          // from defaults that may have changed since.
          setSubmissionDate(stored.submission_date);
          setPaymentDueDate(stored.payment_due_date);
          const storedPeriod = daysBetweenDates(
            stored.submission_date,
            stored.payment_due_date,
          );
          setPaymentPeriodDays(
            (storedPeriod !== null && storedPeriod >= 0
              ? storedPeriod
              : timing.paymentPeriodDays).toString(),
          );
        } else {
          const claimDate = deriveClaimDate(
            selectedYear,
            selectedMonth,
            timing.claimDay,
          );
          setSubmissionDate(claimDate);
          setPaymentDueDate(
            deriveDueDate(claimDate, timing.paymentPeriodDays) ?? "",
          );
          setPaymentPeriodDays(timing.paymentPeriodDays.toString());
        }
      } catch (error) {
        logError("Error loading settings:", error);
        if (!cancelled) setDefaultsError(true);
      } finally {
        if (!cancelled) {
          setLoadingSettings(false);
          setReloadingDefaults(false);
        }
      }
    };

    if (isOpen) {
      loadDefaults();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedMonth, selectedYear, priorHireRetry]);

  // Reset the form when the dialog opens or the period changes. Kept apart from
  // loadDefaults so retrying a failed load does not wipe what the user typed.
  useEffect(() => {
    if (isOpen) {
      setStep("input"); // Reset step on open
      setGeneratedData(null);
      setAiGenerated(false);
      setLoadingStage(0);
      setShowOverrideConfirm(false);
      setRetentionReleaseMode("amount");
      setRetentionReleaseAmount("");
      setRetentionReleasePercent("");
      // Select all variations by default
      setSelectedVariationIds(new Set(variations.map((v) => v.id)));
      setSelectedHireBillingIds(new Set());
      setPreviewHireSourceVariationIds(new Set());
      hireSelectionContextRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedMonth, selectedYear]);

  // SEQ-216: load what other claims already claimed. Own effect, own cancel flag.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPriorHireState("loading");
    setPriorHireData(null);

    const loadPriorHire = async () => {
      try {
        const data = await loadHireClaimedElsewhere(supabase, {
          projectId: project.id,
          year: selectedYear,
          month: selectedMonth,
          isCancelled: () => cancelled,
        });
        if (cancelled || !data) return;
        setPriorHireData({
          ...data,
          stamp: periodStamp(selectedYear, selectedMonth, data.currentReportId),
        });
        setPriorHireState("ready");
      } catch (err) {
        logError("Earlier hire claims load error:", err);
        if (!cancelled) setPriorHireState("error");
      }
    };
    loadPriorHire();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedMonth, selectedYear, project.id, priorHireRetry]);

  // Compute financial preview when month/year changes
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const computePreview = async () => {
      setLoadingPreview(true);
      setPreviewError(false);
      try {
        // Fetch categories with subcategories
        const { data: cats, error: catsError } = await supabase
          .from("claims_categories")
          .select("*, claims_subcategories(*)")
          .eq("project_id", project.id);
        if (catsError) throw catsError;

        const subIds: string[] = [];
        const categoryRows = (cats || []) as ClaimCategoryQueryRow[];

        categoryRows.forEach((c) => {
          (c.claims_subcategories || []).forEach((s) => subIds.push(s.id));
        });

        // Fetch every claims_progress increment; the selected month's rows give P
        // (progress this claim), all rows up to and including it give CumP.
        const { data: progress, error: progressError } = await supabase
          .from("claims_progress")
          .select("amount, claim_month, claim_year")
          .in("subcategory_id", subIds.length > 0 ? subIds : ["__none__"]);

        if (progressError) throw progressError;

        let contractVal = 0;
        let cumulativeProgress = 0;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        (progress || []).forEach((p: any) => {
          const amt = Number(p.amount || 0);
          const pMonth = Number(p.claim_month || 0);
          const pYear = Number(p.claim_year || 0);
          if (
            pYear < selectedYear ||
            (pYear === selectedYear && pMonth <= selectedMonth)
          ) {
            cumulativeProgress += amt;
          }
          if (pYear === selectedYear && pMonth === selectedMonth) {
            contractVal += amt;
          }
        });

        const { data: hireBillingRows, error: hireBillingError } = await supabase
          .from("claims_hire_billing")
          .select(`
            id,
            hire_register_id,
            billing_month,
            billing_year,
            weeks_in_period,
            amount,
            included_in_claim,
            claim_report_id,
            paid,
            paid_amount,
            sub_docket_no,
            billing_status,
            claims_hire_register (
              source_variation_id,
              docket_no,
              item,
              item_description,
              qty,
              levels,
              cost_per_week,
              start_date,
              end_date,
              status,
              claims_variations (
                dashpivot_removed_at,
                discount_percent
              )
            )
          `)
          .eq("project_id", project.id)
          // No status filter: every row is listed, and isHireRowClaimable decides
          // which can be ticked (paid, claimed elsewhere or unknown statuses
          // render unselectable). What earlier claims already claimed is loaded
          // separately for A (computeHireClaimedToDate).
          .order("sub_docket_no", { ascending: true });

        if (hireBillingError) throw hireBillingError;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase join typing is nested dynamically
        const getHireRegister = (row: any) => {
          const rel = row?.claims_hire_register;
          return Array.isArray(rel) ? rel[0] : rel;
        };

        // Filtered once, at the pool, so both consumers below are covered by
        // construction: hire sourced from a variation flagged as gone from
        // Dashpivot must not reach the preview or the generated claim.
        const claimableHireBillingRows = excludeHireRowsWithRemovedParent(
          hireBillingRows || [],
        );

        const hireRows = claimableHireBillingRows
          .filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase join typing is nested dynamically
            (row: any) =>
              Number(row.billing_year || 0) < selectedYear ||
              (Number(row.billing_year || 0) === selectedYear &&
                Number(row.billing_month || 0) <= selectedMonth),
          )
          .map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase join typing is nested dynamically
          (row: any): HireBillingPreviewRow => {
            const register = getHireRegister(row);
            const billingMonth = Number(row.billing_month || selectedMonth);
            const billingYear = Number(row.billing_year || selectedYear);
            const storedWeeksInPeriod = Number(row.weeks_in_period || 0);
            const storedAmount = Number(row.amount || 0);
            const recalculated = calculateHireBillingPeriod({
              billingMonth,
              billingYear,
              startDate: register?.start_date || null,
              endDate: register?.end_date || null,
              costPerWeek: Number(register?.cost_per_week || 0),
              qty: Number(register?.qty || 0),
              levels: Number(register?.levels || 0),
              fallbackWeeks: storedWeeksInPeriod,
              fallbackAmount: storedAmount,
              // SEQ-169: the preview must apply the parent variation's discount
              // exactly as the edge function does, or the figure on screen and
              // the figure in the generated PDF disagree.
              discountPercent: embeddedVariationDiscountPercent(
                register?.claims_variations,
              ),
            });
            const status = row.billing_status || "pending";
            const isPending = status === "pending";
            const isPaid = Boolean(row.paid) || status === "paid";
            // SEQ-145 Amendment 1 §2.3: paid rows are never claimable, whatever
            // billing_status reads (it never transitions to 'paid' in prod data).
            const claimable = isHireRowClaimable(
              {
                paid: row.paid,
                billing_status: row.billing_status,
                claim_report_id: row.claim_report_id,
              },
              existingReport?.id || null,
            );
            const isOutstanding =
              claimable &&
              isPending &&
              (billingYear < selectedYear ||
                (billingYear === selectedYear && billingMonth < selectedMonth));
            const amount = claimable ? recalculated.amount : storedAmount;
            const weeksInPeriod = claimable
              ? recalculated.weeksInPeriod
              : storedWeeksInPeriod;

            return {
              id: row.id,
              hireRegisterId: row.hire_register_id,
              subDocketNo: row.sub_docket_no || register?.docket_no || "-",
              sourceDocketNo: register?.docket_no || "-",
              item: register?.item_description || register?.item || "Hire item",
              billingMonth,
              billingYear,
              periodStartDate: recalculated.periodStartDate,
              periodEndDate: recalculated.periodEndDate,
              periodLabel: recalculated.periodLabel,
              weeksInPeriod,
              storedWeeksInPeriod,
              amount,
              storedAmount,
              status,
              includedInClaim: Boolean(row.included_in_claim),
              claimReportId: row.claim_report_id || null,
              paid: isPaid,
              paidAmount: Number(row.paid_amount || 0),
              isOutstanding,
              canSelect: claimable,
              defaultSelected: claimable,
              wasRecalculated:
                Math.abs(amount - storedAmount) > 0.01 ||
                Math.abs(weeksInPeriod - storedWeeksInPeriod) > 0.01,
            };
          },
        );

        if (cancelled) return;

        setPreviewContractValue(contractVal);
        setPreviewCumulativeProgress(cumulativeProgress);
        setPreviewHireRows(hireRows);
        setPreviewStamp(periodStamp(selectedYear, selectedMonth, existingReport?.id));

        const selectionContext = `${project.id}:${selectedYear}-${selectedMonth}:${existingReport?.id || "new"}`;
        const defaultHireSelection = new Set(
          hireRows.filter((row) => row.defaultSelected).map((row) => row.id),
        );
        const availableHireIds = new Set(hireRows.map((row) => row.id));
        setSelectedHireBillingIds((current) => {
          if (hireSelectionContextRef.current !== selectionContext) {
            hireSelectionContextRef.current = selectionContext;
            return defaultHireSelection;
          }

          return new Set([...current].filter((id) => availableHireIds.has(id)));
        });

        const hireSourceVariationIds = new Set(
          claimableHireBillingRows
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase join typing is nested dynamically
            .map((row: any) => getHireRegister(row)?.source_variation_id)
            .filter((id): id is string => Boolean(id)),
        );
        setPreviewHireSourceVariationIds(hireSourceVariationIds);
      } catch (err) {
        logError("Preview compute error:", err);
        if (!cancelled) {
          setPreviewError(true);
          setPreviewHireRows([]);
          setPreviewHireSourceVariationIds(new Set());
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };
    computePreview();

    return () => {
      cancelled = true;
    };
  }, [
    priorHireRetry,
    isOpen,
    selectedMonth,
    selectedYear,
    project.id,
    existingReport?.id,
  ]);

  useEffect(() => {
    const variationPreview = calculateVariationClaimPreview({
      variations: variations,
      selectedVariationIds,
      includeVariations,
      claimMonth: selectedMonth,
      claimYear: selectedYear,
      hireSourceVariationIds: previewHireSourceVariationIds,
    });

    setPreviewVarsThisMonth(variationPreview.thisMonth);
    setPreviewVarsOutstanding(variationPreview.outstanding);
  }, [
    variations,
    selectedVariationIds,
    includeVariations,
    selectedMonth,
    selectedYear,
    previewHireSourceVariationIds,
  ]);

  // Loading stage cycling effect
  useEffect(() => {
    if (step !== "generating") return;

    const stages = [
      { delay: 0 }, // Stage 0: Preparing
      { delay: 2000 }, // Stage 1: Calculating
      { delay: 4000 }, // Stage 2: Fetching PDFs
      { delay: 8000 }, // Stage 3: Compiling
      { delay: 15000 }, // Stage 4: AI Drafting
      { delay: 25000 }, // Stage 5: Almost done
    ];

    const timers: ReturnType<typeof setTimeout>[] = [];
    stages.forEach((stage, index) => {
      const timer = setTimeout(() => setLoadingStage(index), stage.delay);
      timers.push(timer);
    });

    return () => timers.forEach((t) => clearTimeout(t));
  }, [step]);

  // Handle file upload
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, isStatutory = false) => {
      const files = e.target.files;
      if (!files) return;

      for (const file of Array.from(files)) {
        const validTypes = [
          "application/pdf",
          "image/png",
          "image/jpeg",
          "image/jpg",
        ];
        if (!validTypes.includes(file.type)) {
          addToast({ type: "error", title: `Invalid file type: ${file.name}` });
          continue;
        }

        if (file.size > 5 * 1024 * 1024) {
          addToast({
            type: "error",
            title: `File too large: ${file.name} (max 5MB)`,
          });
          continue;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1];
          const uploadedFile: UploadedFile = {
            name: file.name,
            type: file.type,
            base64,
            size: file.size,
          };

          if (isStatutory) {
            setStatutoryFile(uploadedFile);
          } else {
            setUploadedFiles((prev) => [...prev, uploadedFile]);
          }
        };
        reader.readAsDataURL(file);
      }
      e.target.value = "";
    },
    [addToast],
  );

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // SEQ-169 claim timing handlers.
  //
  // The three fields are kept mutually consistent: the payment period always
  // shows the calendar days between the two dates. Moving the claim date moves
  // the due date, until the operator edits the due date directly, after which
  // the claim date leaves it alone and the period follows instead. Typing a
  // period is an explicit instruction, so it releases that lock.
  const effectivePeriodDays = useMemo(() => {
    const actual = daysBetweenDates(submissionDate, paymentDueDate);
    if (actual !== null && actual >= 0) return actual;
    return resolvePaymentPeriodDays(
      paymentPeriodDays,
      timingDefaults.paymentPeriodDays,
    );
  }, [
    submissionDate,
    paymentDueDate,
    paymentPeriodDays,
    timingDefaults.paymentPeriodDays,
  ]);

  const handleSubmissionDateChange = (value: string) => {
    setSubmissionDate(value);

    if (dueDateEditedRef.current) {
      const period = daysBetweenDates(value, paymentDueDate);
      if (period !== null && period >= 0) setPaymentPeriodDays(period.toString());
      return;
    }

    const period = resolvePaymentPeriodDays(
      paymentPeriodDays,
      timingDefaults.paymentPeriodDays,
    );
    const nextDue = deriveDueDate(value, period);
    if (nextDue) setPaymentDueDate(nextDue);
  };

  const handlePaymentDueDateChange = (value: string) => {
    dueDateEditedRef.current = true;
    setPaymentDueDate(value);

    const period = daysBetweenDates(submissionDate, value);
    if (period !== null && period >= 0) setPaymentPeriodDays(period.toString());
  };

  const handlePaymentPeriodChange = (value: string) => {
    setPaymentPeriodDays(value);
    if (value.trim() === "") return;

    const nextDue = deriveDueDate(
      submissionDate,
      resolvePaymentPeriodDays(value, timingDefaults.paymentPeriodDays),
    );
    if (nextDue) {
      dueDateEditedRef.current = false;
      setPaymentDueDate(nextDue);
    }
  };

  // SEQ-169: record the timing this claim was actually issued with, and the
  // project defaults in force at the time. The edge function still derives the
  // period by subtracting the two dates, so this is a snapshot for later
  // readers rather than an input to the document, and a failure here must not
  // fail a claim that generated correctly. On failure both columns stay NULL,
  // which is exactly the state every reader already falls back from.
  const snapshotClaimTiming = useCallback(async () => {
    try {
      const { data: saved, error: lookupError } = await claimTimingDb
        .from("claims_reports")
        .select("id")
        .eq("project_id", project.id)
        .eq("claim_month", selectedMonth)
        .eq("claim_year", selectedYear)
        .maybeSingle();

      if (lookupError) throw lookupError;
      if (!saved) {
        throw new Error("No saved report found for the generated claim period");
      }

      const { error: updateError } = await claimTimingDb
        .from("claims_reports")
        .update({
          payment_period_days: effectivePeriodDays,
          timing_defaults: {
            default_claim_day: timingDefaults.claimDay,
            payment_period_days: timingDefaults.paymentPeriodDays,
          },
        })
        .eq("id", saved.id);

      if (updateError) throw updateError;
    } catch (error) {
      logError("Claim timing snapshot failed:", error);
    }
  }, [
    project.id,
    selectedMonth,
    selectedYear,
    effectivePeriodDays,
    timingDefaults,
  ]);

  // Generate Handler
  const handleGenerate = async (
    skipStatutoryWarning = false,
    skipOverrideConfirm = false,
  ) => {
    if (!submissionDate) {
      addToast({ type: "error", title: "Please enter submission date" });
      return;
    }
    if (!paymentDueDate) {
      addToast({ type: "error", title: "Please enter payment due date" });
      return;
    }

    // Warn if existing report exists (override confirmation)
    if (existingReport && !skipOverrideConfirm) {
      setShowOverrideConfirm(true);
      return;
    }

    // Warn if no statutory declaration attached
    if (!statutoryFile && !skipStatutoryWarning) {
      setShowStatutoryWarning(true);
      return;
    }

    setStep("generating");
    setLoadingStage(0);
    setMergeState('idle');
    try {
      const payload = {
        projectId: project.id,
        claimMonth: selectedMonth,
        claimYear: selectedYear,
        submissionDate,
        paymentDueDate,
        retentionPercentage: retentionPct,
        retentionReleaseAmount: requestedRetentionRelease,
        notes,
        uploadedFiles,
        statutoryDeclaration: statutoryFile,
        selectedVariationIds: Array.from(selectedVariationIds),
        selectedHireBillingIds: selectedHireRows.map((row) => row.id),
        // Amendment 4: the stable identity of each selected hire line. The edge
        // resolves these against current rows, so a sync churning the row id
        // between now and generation can no longer 400 ("could not be found").
        // Derived from the fetched preview rows (id-in-Set → tuple), so the
        // keys reflect exactly what the user ticked. hire_register_id is NOT
        // NULL in the schema, so every selected row yields a key.
        selectedHireKeys: selectedHireRows.map((row) => ({
          hire_register_id: row.hireRegisterId,
          billing_month: row.billingMonth,
          billing_year: row.billingYear,
        })),
        // SEQ-218: unpaid months from earlier claims the user chose to re-claim.
        reclaimHireKeys: selectedReclaimRows.map((r) => ({
          hire_register_id: r.row.hireRegisterId,
          billing_month: r.row.billingMonth,
          billing_year: r.row.billingYear,
        })),
        includeVariations,
      };

      const { data, error } = await supabase.functions.invoke(
        "generate-progress-claim",
        {
          body: { ...payload, ai_model: aiModel },
        },
      );

      if (error) throw error;

      if (data?.pdfBase64) {
        setGeneratedData(data);
        setAiGenerated(data.aiGenerated ?? false);
        if (data.emailDraft) {
          setEmailSubject(data.emailDraft.subject);
          setEmailBody(data.emailDraft.body);
        }
        setStep("review");
        addToast({
          type: "success",
          title: "Report Generated! Review draft email below.",
        });
        await snapshotClaimTiming();
        // Trigger reports tab refresh
        onReportGenerated?.();

        // Log AI credit usage if token data is available
        if (data.usage) {
          logAiUsage("progress_claim", data.usage);
        } else {
          logAiUsage("progress_claim");
        }
      } else {
        throw new Error("No data returned");
      }
    } catch (error) {
      logError("Error generating:", error);
      addToast({
        type: "error",
        title: "Failed to generate report",
        message: error instanceof Error ? error.message : "Unknown error",
      });
      setStep("input");
    }
  };

  const downloadPdf = () => {
    if (!generatedData) return;
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${generatedData.pdfBase64}`;
    link.download = generatedData.filename || "Claim.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Smart merge: combines base report + all variation PDFs into one download
  const mergeAndDownload = async () => {
    if (!generatedData) return;

    const variationUrls = (generatedData.variationsPdfUrls || []).filter(Boolean) as string[];

    // No variations, just download the base report directly
    if (variationUrls.length === 0) {
      downloadPdf();
      return;
    }

    setMergeState('merging');
    setMergeProgress({ current: 0, total: variationUrls.length });

    try {
      // PDFDocument is imported statically at file top (avoids Vite dynamic import cache issues)

      // Decode the base report from base64
      const mainBinary = atob(generatedData.pdfBase64);
      const mainBytes = new Uint8Array(mainBinary.length);
      for (let i = 0; i < mainBinary.length; i++) {
        mainBytes[i] = mainBinary.charCodeAt(i);
      }
      const mergedDoc = await PDFDocument.load(mainBytes);

      // Download and merge each variation PDF
      for (let idx = 0; idx < variationUrls.length; idx++) {
        setMergeProgress({ current: idx + 1, total: variationUrls.length });
        try {
          const res = await fetch(variationUrls[idx]);
          if (!res.ok) {
            console.warn(`Failed to fetch variation PDF ${idx + 1}: HTTP ${res.status}`);
            continue;
          }
          const varBytes = new Uint8Array(await res.arrayBuffer());
          const varDoc = await PDFDocument.load(varBytes, { updateMetadata: false });
          const pages = await mergedDoc.copyPages(varDoc, varDoc.getPageIndices());
          pages.forEach((p) => mergedDoc.addPage(p));
          console.log(`Merged ${pages.length} pages from variation ${idx + 1}`);
        } catch (varErr) {
          logError(`Error merging variation ${idx + 1}:`, varErr);
          // Continue with remaining
        }
      }

      // Append deferred statutory declaration LAST (after all variations)
      if (generatedData.statutoryDeclarationData?.base64) {
        try {
          const statData = generatedData.statutoryDeclarationData;
          const statBinary = atob(statData.base64);
          const statBytes = new Uint8Array(statBinary.length);
          for (let i = 0; i < statBinary.length; i++) {
            statBytes[i] = statBinary.charCodeAt(i);
          }

          if (statData.type === 'application/pdf') {
            const statDoc = await PDFDocument.load(statBytes, { updateMetadata: false });
            const statPages = await mergedDoc.copyPages(statDoc, statDoc.getPageIndices());
            statPages.forEach((p) => mergedDoc.addPage(p));
            console.log(`Appended statutory declaration (${statPages.length} pages) as final pages`);
          } else if (statData.type.startsWith('image/')) {
            // Handle image statutory declaration (PNG/JPEG)
            let statImage;
            if (statData.type === 'image/png') {
              statImage = await mergedDoc.embedPng(statBytes);
            } else {
              statImage = await mergedDoc.embedJpg(statBytes);
            }
            if (statImage) {
              const imgPage = mergedDoc.addPage();
              const { width, height } = imgPage.getSize();
              const dims = statImage.scaleToFit(width - 100, height - 100);
              imgPage.drawImage(statImage, {
                x: (width - dims.width) / 2,
                y: (height - dims.height) / 2,
                width: dims.width,
                height: dims.height,
              });
              console.log('Appended statutory declaration image as final page');
            }
          }
        } catch (statErr) {
          logError('Error appending deferred statutory declaration:', statErr);
          // Non-fatal: continue with download
        }
      }

      // Serialize the merged document
      const mergedBytes = await mergedDoc.save();
      const blob = new Blob([mergedBytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // Re-upload the COMPLETE merged PDF to storage so claims_reports has the full version
      try {
        const monthFolder = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;
        const baseName = (generatedData.filename || "Claim.pdf").replace(/\.pdf$/i, "");
        const mergedPath = `${project.id}/${monthFolder}/${baseName}_merged_v${Date.now()}.pdf`;

        const { error: uploadErr } = await supabase.storage
          .from("progress-claim-pdfs")
          .upload(mergedPath, mergedBytes, {
            contentType: "application/pdf",
            upsert: true,
          });

        if (!uploadErr) {
          const { data: urlData } = supabase.storage
            .from("progress-claim-pdfs")
            .getPublicUrl(mergedPath);

          if (urlData?.publicUrl) {
            // Update claims_reports with the complete merged PDF URL
            await supabase
              .from("claims_reports")
              .update({
                pdf_url: urlData.publicUrl,
                pdf_filename: generatedData.filename || "Claim.pdf",
              })
              .eq("project_id", project.id)
              .eq("claim_month", selectedMonth)
              .eq("claim_year", selectedYear);

            console.log("Merged PDF uploaded and claims_reports updated");
          }
        } else {
          logWarn("Failed to upload merged PDF:", uploadErr);
        }
      } catch (reuploadErr) {
        // Non-fatal: the download still works, just the saved version stays partial
        logWarn("Merged PDF re-upload failed:", reuploadErr);
      }

      // Trigger reports tab refresh so the updated PDF URL is visible
      onReportGenerated?.();

      // Trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = generatedData.filename || "Claim.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setMergeState('done');
      console.log(`Merge complete. Total pages: ${mergedDoc.getPageCount()}`);
      addToast({
        type: "success",
        title: `Complete report downloaded (${mergedDoc.getPageCount()} pages)`,
      });
    } catch (err) {
      logError("PDF merge failed:", err);
      setMergeState('error');
      addToast({
        type: "error",
        title: "Merge failed, downloading base report instead",
        message: err instanceof Error ? err.message : "Unknown error",
      });
      // Fallback: download base report only
      downloadPdf();
    }
  };

  const openEmailClient = () => {
    if (!emailSubject || !emailBody) return;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(
      emailSubject,
    )}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
  };

  if (!isOpen) return null;

  // Header Title
  const modalTitle = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        paddingRight: "16px",
      }}
    >
      <div>
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "var(--text-primary)",
            marginBottom: "4px",
          }}
        >
          {step === "review" ? "Ready to Send" : "Send Claim Report"}
        </h2>
        <p
          style={{
            fontSize: "0.875rem",
            color: "var(--text-secondary)",
            maxWidth: "24rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            margin: 0,
          }}
        >
          {project.job_location || project.job_address}
        </p>
      </div>
      <div style={{ textAlign: "right" }}>
        <div
          style={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: "4px",
          }}
        >
          Claim Period
        </div>
        <div
          style={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "var(--color-primary)",
          }}
        >
          {MONTHS[selectedMonth - 1]} {selectedYear}
        </div>
      </div>
    </div>
  );

  const requestedRetentionRelease =
    retentionReleaseMode === "percent"
      ? Math.round(
          Math.max(0, availableRetentionRelease) *
            (Math.max(0, Number(retentionReleasePercent || 0)) / 100) *
            100,
        ) / 100
      : Math.max(0, Number(retentionReleaseAmount || 0));
  const retentionReleaseExceedsAvailable =
    requestedRetentionRelease > availableRetentionRelease &&
    requestedRetentionRelease > 0;
  const formatMoney = (amount: number) =>
    amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const remainingAfterRelease = Math.max(
    0,
    availableRetentionRelease - requestedRetentionRelease,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="xl"
      // Hide standard footer actions in 'review' step or 'generating'
      confirmText={step === "review" ? "Done" : "Generate Draft"}
      cancelText={step === "review" ? "" : "Cancel"}
      confirmDisabled={
        step === "generating" ||
        loadingSettings ||
        (step !== "review" && !previewReady)
      }
      onConfirm={step === "review" ? onClose : () => handleGenerate()}
      hideFooter={step === "generating"}
    >
      {loadingSettings || step === "generating" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "64px 32px",
            background:
              "linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)",
            borderRadius: "var(--border-radius-lg)",
            minHeight: "300px",
          }}
        >
          {/* Animated Loading Stages */}
          {(() => {
            const stages = [
              {
                icon: FileText,
                message: "Preparing your claim...",
                color: "var(--color-primary)",
              },
              {
                icon: Calculator,
                message: "Calculating totals...",
                color: "#10b981",
              },
              {
                icon: Download,
                message: "Fetching variation PDFs...",
                color: "#8b5cf6",
              },
              {
                icon: Layers,
                message: "Compiling documents...",
                color: "#f59e0b",
              },
              {
                icon: Sparkles,
                message: "AI drafting your email...",
                color: "#ec4899",
              },
              {
                icon: CheckCircle2,
                message: "Almost done...",
                color: "#22c55e",
              },
            ];
            const currentStage = loadingSettings
              ? 0
              : Math.min(loadingStage, stages.length - 1);
            const { icon: Icon, message, color } = stages[currentStage];

            return (
              <>
                {/* Icon with pulse animation */}
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    background: `${color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "24px",
                    animation: "pulse 2s ease-in-out infinite",
                  }}
                >
                  <Icon
                    size={36}
                    color={color}
                    style={{ animation: "iconBounce 1s ease-in-out infinite" }}
                  />
                </div>

                {/* Stage Message */}
                <h3
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                    marginBottom: "8px",
                    textAlign: "center",
                    animation: "fadeIn 0.3s ease-out",
                  }}
                  key={currentStage} // Force re-render for animation
                >
                  {loadingSettings ? "Loading Settings..." : message}
                </h3>

                {/* Progress Dots */}
                {!loadingSettings && (
                  <div
                    style={{ display: "flex", gap: "8px", marginTop: "16px" }}
                  >
                    {stages.map((_, index) => (
                      <div
                        key={index}
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background:
                            index <= currentStage
                              ? color
                              : "var(--border-color)",
                          transition: "all 0.3s ease",
                          transform:
                            index === currentStage ? "scale(1.3)" : "scale(1)",
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Subtle hint text */}
                <p
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "0.85rem",
                    marginTop: "20px",
                    textAlign: "center",
                  }}
                >
                  {loadingSettings
                    ? "Please wait..."
                    : "This may take a moment..."}
                </p>
              </>
            );
          })()}

          {/* CSS Keyframes */}
          <style>{`
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.05); opacity: 0.8; }
            }
            @keyframes iconBounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-4px); }
            }
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      ) : step === "review" && generatedData ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div
            style={{
              background: "var(--bg-success-light)",
              border: "1px solid var(--color-success)",
              borderRadius: "8px",
              padding: "16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                background: "var(--color-success)",
                padding: "8px",
                borderRadius: "50%",
                color: "white",
              }}
            >
              <Check size={20} />
            </div>
            <div>
              <h4
                style={{
                  margin: "0 0 4px 0",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Report Generated Successfully
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.875rem",
                  color: "var(--text-secondary)",
                }}
              >
                Review the AI-drafted email below, then download your PDF and
                open Outlook.
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <button
              onClick={mergeAndDownload}
              disabled={mergeState === 'merging'}
              className="btn btn-secondary"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                height: "48px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {mergeState === 'merging' ? (
                <>
                  <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
                  Merging {mergeProgress.current}/{mergeProgress.total}...
                </>
              ) : mergeState === 'done' ? (
                <>
                  <CheckCircle2 size={18} />
                  Downloaded ✓
                </>
              ) : (
                <>
                  <FileText size={18} />
                  {generatedData.variationsPdfUrls?.filter(Boolean).length
                    ? "Download Complete Report"
                    : "Download PDF Report"}
                </>
              )}
            </button>
            <button
              onClick={openEmailClient}
              className="btn btn-primary"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                height: "48px",
              }}
            >
              <Upload size={18} />
              Open Email Client
            </button>
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                fontSize: "0.75rem",
                color: "var(--text-muted)",
              }}
            >
              Opens your default email app (e.g. Outlook Classic/New, Mail).
            </div>

            {/* Merge progress bar */}
            {mergeState === 'merging' && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  background: "rgba(59, 130, 246, 0.08)",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                  fontSize: "0.8rem",
                  color: "var(--color-info)",
                }}>
                  <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                  Merging variation PDFs into report...
                </div>
                <div style={{
                  height: "4px",
                  background: "rgba(59, 130, 246, 0.15)",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%",
                    width: `${mergeProgress.total ? (mergeProgress.current / mergeProgress.total) * 100 : 0}%`,
                    background: "var(--color-info)",
                    borderRadius: "2px",
                    transition: "width 0.3s ease",
                  }} />
                </div>
              </div>
            )}

            {/* Collapsible Individual Downloads (fallback) */}
            {generatedData.variationsPdfUrls &&
              generatedData.variationsPdfUrls.filter(Boolean).length > 0 && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    background: "rgba(59, 130, 246, 0.04)",
                    border: "1px solid rgba(59, 130, 246, 0.12)",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => setShowIndividualDownloads(!showIndividualDownloads)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "var(--text-secondary)",
                    }}
                  >
                    {showIndividualDownloads ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                    <Layers size={14} />
                    Individual Variation Downloads
                    <span style={{
                      marginLeft: "auto",
                      fontSize: "0.7rem",
                      color: "var(--text-muted)",
                    }}>
                      {generatedData.variationsPdfUrls.filter(Boolean).length} file(s)
                    </span>
                  </button>
                  {showIndividualDownloads && (
                    <div
                      style={{
                        padding: "0 16px 12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <p style={{
                        fontSize: "0.7rem",
                        color: "var(--text-muted)",
                        margin: "0 0 6px 0",
                      }}>
                        Download variation PDFs separately if the merged report
                        didn't include them.
                      </p>
                      {generatedData.variationsPdfUrls
                        .filter(Boolean)
                        .map((url, idx) => (
                          <a
                            key={idx}
                            href={url!}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "0.8rem",
                              color: "var(--color-info)",
                              textDecoration: "none",
                              padding: "6px 10px",
                              borderRadius: "6px",
                              background: "rgba(59, 130, 246, 0.08)",
                              transition: "background 0.2s",
                            }}
                          >
                            <Download size={14} />
                            Variation Forms
                            {generatedData.variationsPdfUrls!.filter(Boolean)
                              .length > 1 &&
                              ` (Part ${idx + 1})`}
                          </a>
                        ))}
                    </div>
                  )}
                </div>
              )}
          </div>

          <div
            style={{
              background: "var(--bg-tertiary)",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid var(--border-color)",
            }}
          >
            <h4
              style={{
                margin: "0 0 16px 0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: "1.2rem" }}>✨</span> Smart Email Draft
            </h4>

            <div style={{ marginBottom: "16px" }}>
              <label className="form-label">Subject Line</label>
              <input
                type="text"
                className="form-input"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label className="form-label">Email Body</label>
              <textarea
                className="form-input"
                style={{ height: "200px", fontFamily: "inherit" }}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
              {aiGenerated ? (
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    marginTop: "4px",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Sparkles size={12} />
                  This draft was generated by AI. Please verify all details
                  before sending.
                </p>
              ) : (
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    marginTop: "4px",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <FileText size={12} />A standard template was used for this
                  draft.
                </p>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                padding: "12px",
                background: "var(--bg-card)",
                borderRadius: "6px",
                border: "1px dashed var(--border-color)",
                fontSize: "0.80rem",
                color: "var(--text-secondary)",
              }}
            >
              <div
                style={{
                  minWidth: "20px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                ⚠️
              </div>
              <p style={{ margin: 0 }}>
                <b>Important:</b> Web browsers cannot automatically stick the
                PDF into your email.
                <br />
                After clicking "Open Email Client", you must{" "}
                <b>drag and drop</b> the downloaded PDF file into the email
                manually.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {/* Section 1: Claim Period & Dates */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <h3
              style={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Calendar size={16} color="var(--color-primary)" />
              Claim Details
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label className="form-label">Month</label>
                <div style={{ position: "relative" }}>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="form-input"
                    style={{ appearance: "none" }}
                  >
                    {MONTHS.map((month, i) => (
                      <option key={i} value={i + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--text-muted)",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Year</label>
                <div style={{ position: "relative" }}>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="form-input"
                    style={{ appearance: "none" }}
                  >
                    {[2024, 2025, 2026, 2027].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--text-muted)",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "16px",
              }}
            >
              <div>
                <label className="form-label">Submission</label>
                <input
                  type="date"
                  value={submissionDate}
                  onChange={(e) => handleSubmissionDateChange(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Payment Period (days)</label>
                <input data-seq-private
                  type="number"
                  min="0"
                  max="365"
                  step="1"
                  value={paymentPeriodDays}
                  onChange={(e) => handlePaymentPeriodChange(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  value={paymentDueDate}
                  onChange={(e) => handlePaymentDueDateChange(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Retention %</label>
                <input data-seq-private
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={retentionPct}
                  onChange={(e) =>
                    setRetentionPct(parseFloat(e.target.value) || 0)
                  }
                  className="form-input"
                  disabled={retentionCeilingReached}
                  style={
                    retentionCeilingReached
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : {}
                  }
                />
                {retentionCeilingReached && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginTop: "6px",
                      padding: "6px 10px",
                      background: "rgba(239, 68, 68, 0.1)",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      color: "#ef4444",
                      fontWeight: 600,
                    }}
                  >
                    <AlertTriangle size={14} />
                    Retention ceiling reached ($
                    {accumulatedRetention.toLocaleString()} of $
                    {retentionCeiling.toLocaleString()})
                  </div>
                )}
                {!retentionCeilingReached &&
                  remainingRetention > 0 &&
                  retentionCeiling > 0 && (
                    <div data-seq-private=""
                      style={{
                        marginTop: "6px",
                        fontSize: "0.7rem",
                        color: "var(--text-muted)",
                      }}
                    >
                      Remaining capacity: $
                      {remainingRetention.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      of $
                      {retentionCeiling.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      ceiling
                    </div>
                  )}
              </div>
            </div>

            <div
              style={{
                border: "1px solid #dbeafe",
                borderRadius: "8px",
                background: "#f8fafc",
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <label className="form-label" style={{ marginBottom: 2 }}>
                    Retention Release
                  </label>
                  <div data-seq-private="" style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                    Outstanding held ${formatMoney(availableRetentionRelease)}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setRetentionReleaseMode("percent");
                      setRetentionReleasePercent("100");
                      setRetentionReleaseAmount(availableRetentionRelease.toFixed(2));
                    }}
                    disabled={availableRetentionRelease <= 0}
                    style={{ fontSize: "0.72rem", padding: "6px 10px" }}
                  >
                    Full Balance
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setRetentionReleaseAmount("");
                      setRetentionReleasePercent("");
                    }}
                    disabled={requestedRetentionRelease <= 0}
                    style={{ fontSize: "0.72rem", padding: "6px 10px" }}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div
                role="group"
                aria-label="Retention Release input mode"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  padding: "3px",
                  marginBottom: "12px",
                  borderRadius: "6px",
                  background: "#e2e8f0",
                }}
              >
                {(["amount", "percent"] as RetentionReleaseMode[]).map((mode) => {
                  const active = retentionReleaseMode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setRetentionReleaseMode(mode)}
                      style={{
                        minHeight: "44px",
                        minWidth: "104px",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        background: active ? "#fff" : "transparent",
                        color: active ? "#0f172a" : "#475569",
                        fontSize: "0.86rem",
                        fontWeight: active ? 700 : 600,
                        boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
                      }}
                    >
                      {mode === "amount" ? "Amount" : "Percent"}
                    </button>
                  );
                })}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                  gap: "12px",
                  alignItems: "start",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <input data-seq-private
                    type="number"
                    min="0"
                    step={retentionReleaseMode === "percent" ? "0.01" : "0.01"}
                    value={
                      retentionReleaseMode === "percent"
                        ? retentionReleasePercent
                        : retentionReleaseAmount
                    }
                    onChange={(e) => {
                      if (retentionReleaseMode === "percent") {
                        setRetentionReleasePercent(e.target.value);
                      } else {
                        setRetentionReleaseAmount(e.target.value);
                      }
                    }}
                    className="form-input"
                    placeholder={retentionReleaseMode === "percent" ? "0%" : "$0.00"}
                    inputMode="decimal"
                    style={{
                      minHeight: "76px",
                      fontSize: "1.05rem",
                      fontWeight: 600,
                    }}
                    aria-label={
                      retentionReleaseMode === "percent"
                        ? "Retention Release percent of outstanding held balance"
                        : "Retention Release amount"
                    }
                  />
                  {retentionReleaseMode === "percent" && (
                    <div data-seq-private="" style={{ marginTop: 4, fontSize: "0.68rem", color: "var(--text-muted)" }}>
                      of ${formatMoney(availableRetentionRelease)}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    minHeight: "76px",
                    padding: "10px 16px",
                    borderRadius: "6px",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ fontSize: "0.66rem", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>
                    This Release
                  </div>
                  <div data-seq-private="" style={{ color: "#16a34a", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                    ${formatMoney(requestedRetentionRelease)}
                  </div>
                </div>
                <div
                  style={{
                    minHeight: "76px",
                    padding: "10px 16px",
                    borderRadius: "6px",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ fontSize: "0.66rem", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>
                    Remaining Held
                  </div>
                  <div data-seq-private="" style={{ color: "#475569", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                    ${formatMoney(remainingAfterRelease)}
                  </div>
                </div>
              </div>

              {retentionReleaseExceedsAvailable && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginTop: "10px",
                    padding: "8px 10px",
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.24)",
                    borderRadius: "6px",
                    fontSize: "0.75rem",
                    color: "#92400e",
                    fontWeight: 600,
                  }}
                >
                  <AlertTriangle size={14} aria-hidden="true" />
                  Above tracked outstanding retention. Verify before sending.
                </div>
              )}
            </div>
          </div>

          {/* Financial Preview Card */}
          {(() => {
            const pvSubtotal =
              previewContractValue +
              previewVarsThisMonth +
              previewVarsOutstanding +
              previewInstallHire;
            const pvRelease = requestedRetentionRelease;
            // SEQ-145 new-format page 1: headline is A - B = C, retention on C,
            // then GST. Mirrors generate-progress-claim/claim-summary.ts exactly
            // (parity-tested in src/lib/claimSummary.test.ts).
            const summary = computeClaimSummary({
              cumulativeProgressToDate: previewCumulativeProgress,
              progressThisClaim: previewContractValue,
              paidVariationsTotal: previewPaidVariationsTotal,
              variationsOutstanding: previewVarsOutstanding,
              variationsThisMonth: previewVarsThisMonth,
              installHireThisClaim: previewInstallHire + previewReclaimedHire,
              // SEQ-216: A's hire component is hire CLAIMED to date: the rows
              // ticked for this claim plus hire invoiced by earlier claims.
              // Unticking a row lowers A and C by its amount, exactly as the
              // edge function's computeHireClaimedToDate.
              hireClaimedToDate:
                previewHireClaimed?.total ?? previewInstallHire + previewReclaimedHire,
              paidByBuilderIncGst: previewPaidByBuilderIncGst,
              priorRetentionOnPaidClaims: previewPriorRetentionOnPaidClaims,
              retentionPct: retentionCeilingReached ? 0 : retentionPct,
              remainingRetentionCapacity: retentionCeilingReached
                ? 0
                : remainingRetention > 0
                  ? remainingRetention
                  : Number.POSITIVE_INFINITY,
              retentionReleaseAmount: pvRelease,
            });
            // Ceiling clamp indicator: raw C x pct exceeds the remaining headroom.
            const pvRawRetention =
              Math.max(0, summary.C) *
              ((retentionCeilingReached ? 0 : retentionPct) / 100);
            const pvIsCapped =
              !retentionCeilingReached &&
              remainingRetention > 0 &&
              pvRawRetention > remainingRetention;
            const fmt = (n: number) =>
              n.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });

            return pvSubtotal > 0 || summary.A > 0 || loadingPreview ? (
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                  border: "1px solid #bae6fd",
                  borderRadius: "10px",
                  padding: "16px 18px",
                }}
              >
                <h4
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "#0369a1",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Calculator size={14} aria-hidden="true" /> Financial Preview
                </h4>
                {previewFailed && !loadingPreview && !reloadingDefaults ? (
                  <div
                    role="alert"
                    className="var-status-banner var-status-banner--pending claim-preview-load-error"
                  >
                    <span>
                      Earlier claims did not load, so the total claimed to date is
                      not shown.
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary claim-preview-load-error__retry"
                      onClick={() => setPriorHireRetry((n) => n + 1)}
                    >
                      Try again
                    </button>
                  </div>
                ) : !previewReady ? (
                  <div role="status" className="claim-preview-computing">
                    Computing...
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      fontSize: "0.82rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "#4b5563" }}>
                        Contract Value (this month)
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        ${fmt(previewContractValue)}
                      </span>
                    </div>
                    {includeVariations && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: "#4b5563" }}>
                            Variations This Month
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            ${fmt(previewVarsThisMonth)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: "#4b5563" }}>
                            Variations Outstanding
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            ${fmt(previewVarsOutstanding)}
                          </span>
                        </div>
                      </>
                    )}
                    {previewInstallHire + previewReclaimedHire > 0 && (
                      <div
                        data-testid="install-hire-preview-line"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ color: "#4b5563" }}>
                          Install & Hire
                        </span>
                        <span style={{ fontWeight: 600 }}>
                          ${fmt(previewInstallHire + previewReclaimedHire)}
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderTop: "1px solid #bae6fd",
                        paddingTop: "6px",
                        marginTop: "2px",
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#0c4a6e" }}>
                        Subtotal (Ex GST)
                      </span>
                      <span style={{ fontWeight: 700, color: "#0c4a6e" }}>
                        ${fmt(pvSubtotal)}
                      </span>
                    </div>
                    {/* SEQ-145 payment-claim headline: A - B = C, retention on C, then GST. */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderTop: "2px solid #bae6fd",
                        paddingTop: "8px",
                        marginTop: "4px",
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#0c4a6e" }}>
                        Total claimed to date (A)
                      </span>
                      <span style={{ fontWeight: 700, color: "#0c4a6e" }}>
                        ${fmt(summary.A)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "#dc2626" }}>
                        Less previously certified (B)
                      </span>
                      <span style={{ fontWeight: 600, color: "#dc2626" }}>
                        -${fmt(summary.bReceipts)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "#dc2626" }}>
                        Less retention on paid claims to date (C)
                      </span>
                      <span style={{ fontWeight: 600, color: "#dc2626" }}>
                        -${fmt(summary.retentionHeldToDate)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderTop: "1px solid #bae6fd",
                        paddingTop: "6px",
                        marginTop: "2px",
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#0c4a6e" }}>
                        Total this claim excluding GST (A-B-C)
                      </span>
                      <span style={{ fontWeight: 700, color: "#0c4a6e" }}>
                        ${fmt(summary.C)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: "#dc2626" }}>
                        Retention held (
                        {retentionCeilingReached ? "0" : retentionPct}%)
                        {pvIsCapped && (
                          <span
                            style={{
                              marginLeft: "6px",
                              background: "rgba(245, 158, 11, 0.15)",
                              color: "#d97706",
                              fontSize: "0.68rem",
                              padding: "1px 6px",
                              borderRadius: "4px",
                              fontWeight: 600,
                            }}
                          >
                            CAPPED
                          </span>
                        )}
                      </span>
                      <span style={{ fontWeight: 600, color: "#dc2626" }}>
                        -${fmt(summary.R)}
                      </span>
                    </div>
                    {summary.release > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "#16a34a" }}>
                          Retention release
                        </span>
                        <span style={{ fontWeight: 600, color: "#16a34a" }}>
                          +${fmt(summary.release)}
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: "#dc2626" }}>GST (10%)</span>
                      <span style={{ fontWeight: 600 }}>
                        ${fmt(summary.G)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderTop: "2px solid #bae6fd",
                        paddingTop: "6px",
                        marginTop: "2px",
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#16a34a" }}>
                        Total payable
                      </span>
                      <span style={{ fontWeight: 700, color: "#16a34a" }}>
                        ${fmt(summary.T)}
                      </span>
                    </div>
                    {retentionCeiling > 0 &&
                      (() => {
                        // Josh (27 Jul): true retention held to date = all prior
                        // claims + this claim's retention, matching the overview
                        // card + the ceiling. NOT the paid-only add-back above.
                        const status = retentionCeilingStatus(
                          accumulatedRetention,
                          summary.R,
                          retentionCeiling,
                        );
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: "4px",
                              fontSize: "0.72rem",
                              color: status.overCeiling ? "#d97706" : "#64748b",
                            }}
                          >
                            <span>
                              Retention held to date (incl. this claim) of $
                              {fmt(status.ceiling)} ceiling
                              {status.overCeiling
                                ? ` - EXCEEDS ${formatRetentionCeilingPercent(project.retention_ceiling_percent)}%`
                                : ""}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              ${fmt(status.heldToDate)}
                            </span>
                          </div>
                        );
                      })()}
                  </div>
                )}
              </div>
            ) : null;
          })()}

          {previewHireRows.length > 0 && !loadingPreview && previewFresh && (
            <div
              data-testid="install-hire-preview"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <h4
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "#166534",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    margin: "0 auto 0 0",
                  }}
                >
                  <Wrench size={14} aria-hidden="true" /> Install & Hire
                </h4>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: "0.75rem", padding: "4px 8px" }}
                  onClick={() =>
                    setSelectedHireBillingIds(
                      new Set(
                        mainHireRows
                          .filter((row) => row.canSelect || row.defaultSelected)
                          .map((row) => row.id),
                      ),
                    )
                  }
                >
                  Select Claimable (
                  {mainHireRows.filter((row) => row.canSelect).length})
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: "0.75rem", padding: "4px 8px" }}
                  onClick={() =>
                    setSelectedHireBillingIds(
                      new Set(
                        mainHireRows
                          .filter((row) => row.defaultSelected && !row.canSelect)
                          .map((row) => row.id),
                      ),
                    )
                  }
                >
                  Clear
                </button>
                <div data-seq-private=""
                  style={{
                    fontWeight: 800,
                    color: "#166534",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  ${previewInstallHire.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedHireRows.length} of {mainHireRows.length} selected
                </span>
              </div>
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  borderRadius: "var(--border-radius-md)",
                  border: "1px solid var(--border-color)",
                  padding: "8px",
                  maxHeight: "210px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  fontSize: "0.76rem",
                }}
              >
                {mainHireRows.map((row) => {
                  const isSelected = selectedHireBillingIds.has(row.id);
                  // SEQ-216: attached to another report = already claimed there.
                  const claimedElsewhere =
                    Boolean(row.claimReportId) &&
                    row.claimReportId !== existingReport?.id;
                  const statusLabel = row.paid
                    ? "Paid"
                    : row.status === "included" || claimedElsewhere
                      ? "Claimed"
                      : row.isOutstanding
                        ? "Outstanding"
                        : "Pending";
                  const statusColor = row.paid
                    ? "#16a34a"
                    : row.status === "included" || claimedElsewhere
                      ? "#2563eb"
                      : row.isOutstanding
                        ? "#d97706"
                        : "#64748b";
                  const statusBg = row.paid
                    ? "rgba(22, 163, 74, 0.1)"
                    : row.status === "included" || claimedElsewhere
                      ? "rgba(37, 99, 235, 0.1)"
                      : row.isOutstanding
                        ? "rgba(217, 119, 6, 0.1)"
                        : "rgba(100, 116, 139, 0.1)";

                  return (
                    <label
                      key={row.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "18px minmax(88px, 0.75fr) minmax(160px, 1.5fr) auto",
                        gap: "10px",
                        alignItems: "center",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        cursor: row.canSelect ? "pointer" : "default",
                        opacity: row.canSelect || isSelected ? 1 : 0.72,
                        background: isSelected
                          ? "rgba(22, 163, 74, 0.08)"
                          : row.isOutstanding
                            ? "rgba(217, 119, 6, 0.06)"
                            : "transparent",
                        borderBottom: "1px solid var(--border-color-light)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!row.canSelect}
                        aria-label={
                          claimedElsewhere && !row.paid
                            ? `${row.subDocketNo}, ${row.periodLabel}: claimed on an earlier claim, cannot be selected`
                            : undefined
                        }
                        onChange={() => {
                          const newSet = new Set(selectedHireBillingIds);
                          if (isSelected) {
                            newSet.delete(row.id);
                          } else {
                            newSet.add(row.id);
                          }
                          setSelectedHireBillingIds(newSet);
                        }}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: row.canSelect ? "pointer" : "default",
                        }}
                      />
                      <div
                        title={row.subDocketNo}
                        style={{
                          color: "#166534",
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.subDocketNo}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.item}
                        </div>
                        <div
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "0.7rem",
                            marginTop: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.periodLabel} • {row.weeksInPeriod.toFixed(2)} wk
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.65rem",
                            background: statusBg,
                            color: statusColor,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontWeight: 700,
                          }}
                        >
                          {statusLabel}
                        </span>
                        <span data-seq-private="" style={{ fontWeight: 800, color: "#111827" }}>
                          ${formatMoney(row.amount)}
                        </span>
                      </div>
                    </label>
                  );
                })}
                {mainHireRows.length === 0 && (
                  <div
                    style={{
                      padding: "12px",
                      color: "var(--text-muted)",
                      textAlign: "center",
                    }}
                  >
                    No Install & Hire billing rows for this claim period
                  </div>
                )}
              </div>
              {reclaimRows.length > 0 && (
                <div
                  data-testid="install-hire-reclaim"
                  role="group"
                  aria-labelledby="install-hire-reclaim-heading"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    marginTop: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      flexWrap: "wrap",
                      gap: "4px 10px",
                    }}
                  >
                    <h5
                      id="install-hire-reclaim-heading"
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "var(--text-primary)",
                        margin: 0,
                      }}
                    >
                      Unpaid from earlier claims
                    </h5>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--text-muted)",
                        marginLeft: "auto",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedReclaimRows.length} of {reclaimRows.length} selected
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      margin: 0,
                      lineHeight: 1.4,
                    }}
                  >
                    These months are already in Claimed to Date. Re-claiming one
                    moves it onto this claim so a payment can be recorded here. It
                    keeps its amount and does not change what is due.
                  </p>
                  <div
                    style={{
                      background: "var(--bg-tertiary)",
                      borderRadius: "var(--border-radius-md)",
                      border: "1px solid var(--border-color)",
                      padding: "8px",
                      maxHeight: "210px",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      fontSize: "0.76rem",
                    }}
                  >
                    {reclaimRows.map((entry) => {
                      const { row } = entry;
                      const isSelected = reclaimTouched.has(row.id)
                        ? Boolean(reclaimTouched.get(row.id))
                        : entry.wasReclaimedHere;
                      // The row's accessible name keeps the full wording; the badge
                      // is short so it never wraps into a chip.
                      const claimLabel = entry.fromClaimNumber
                        ? `Claimed on claim ${entry.fromClaimNumber}, unpaid`
                        : "Claimed on an earlier claim, unpaid";
                      const badgeText = entry.fromClaimNumber
                        ? `Claim ${entry.fromClaimNumber}, unpaid`
                        : "Earlier claim, unpaid";
                      const badge = (
                        <span
                          title={claimLabel}
                          style={{
                            fontSize: "0.75rem",
                            background: "rgba(37, 99, 235, 0.1)",
                            color: "#1d4ed8",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            justifySelf: "start",
                          }}
                        >
                          {badgeText}
                        </span>
                      );
                      const price = (
                        <span
                          data-seq-private=""
                          style={{ fontWeight: 800, color: "#111827", whiteSpace: "nowrap" }}
                        >
                          ${formatMoney(row.storedAmount)}
                        </span>
                      );
                      return (
                        <label
                          key={row.id}
                          style={{
                            display: "grid",
                            gridTemplateColumns: narrowViewport
                              ? "18px minmax(0, 1fr) auto"
                              : "18px minmax(88px, 0.75fr) minmax(160px, 1.5fr) auto",
                            gap: "10px",
                            alignItems: "center",
                            padding: "8px 10px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            background: isSelected
                              ? "rgba(22, 163, 74, 0.08)"
                              : "transparent",
                            borderBottom: "1px solid var(--border-color-light)",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            aria-label={`${row.subDocketNo}, ${row.periodLabel}: ${claimLabel}`}
                            onChange={() => {
                              const next = new Map(reclaimTouched);
                              next.set(row.id, !isSelected);
                              setReclaimTouched(next);
                            }}
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          {narrowViewport ? (
                            <>
                              <div style={{ minWidth: 0, display: "grid", gap: "2px" }}>
                                <div
                                  title={row.subDocketNo}
                                  style={{
                                    color: "#166534",
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.subDocketNo}
                                </div>
                                <div
                                  title={row.item}
                                  style={{
                                    color: "var(--text-primary)",
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.item}
                                </div>
                                <div
                                  style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.7rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.periodLabel} • {row.storedWeeksInPeriod.toFixed(2)} wk
                                </div>
                                {badge}
                              </div>
                              {price}
                            </>
                          ) : (
                            <>
                              <div
                                title={row.subDocketNo}
                                style={{
                                  color: "#166534",
                                  fontWeight: 700,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.subDocketNo}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div
                                  title={row.item}
                                  style={{
                                    color: "var(--text-primary)",
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.item}
                                </div>
                                <div
                                  style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.7rem",
                                    marginTop: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.periodLabel} • {row.storedWeeksInPeriod.toFixed(2)} wk
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: "8px",
                                }}
                              >
                                {badge}
                                {price}
                              </div>
                            </>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            style={{ height: "1px", background: "var(--border-color-light)" }}
          ></div>

          {/* Section 2: Variations Info */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <h3
              style={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FileText size={16} color="#8b5cf6" />
              Variations to Include
            </h3>
            <p
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                margin: "0 0 8px 0",
                fontStyle: "italic",
              }}
            >
              Select which variations to include. Selection controls financial totals, breakdown table, and attached Dashpivot forms.
            </p>

            {/* Select All / Deselect All */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
                marginBottom: "8px",
              }}
            >
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: "0.75rem", padding: "4px 8px" }}
                onClick={() =>
                  setSelectedVariationIds(new Set(variations.map((v) => v.id)))
                }
                disabled={!includeVariations}
              >
                Select All ({variations.length})
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: "0.75rem", padding: "4px 8px" }}
                onClick={() => setSelectedVariationIds(new Set())}
                disabled={!includeVariations}
              >
                Deselect All
              </button>

              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    color: "var(--text-primary)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!includeVariations}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setIncludeVariations(!isChecked);
                      if (isChecked) {
                        setSelectedVariationIds(new Set());
                      } else {
                        setSelectedVariationIds(
                          new Set(variations.map((v) => v.id)),
                        );
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  />
                  Do not include variations
                </label>

                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                  }}
                >
                  {selectedVariationIds.size} of {variations.length} selected
                </span>
              </div>
            </div>

            {/* Variations Checklist */}
            <div
              style={{
                background: "var(--bg-tertiary)",
                borderRadius: "var(--border-radius-md)",
                border: "1px solid var(--border-color)",
                padding: "8px",
                maxHeight: "180px",
                overflowY: "auto",
              }}
            >
              {variations.length === 0 ? (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "var(--text-muted)",
                    fontSize: "0.875rem",
                  }}
                >
                  No variations for this project
                </div>
              ) : (
                variations.map((v) => {
                  const isSelected = selectedVariationIds.has(v.id);
                  const isDisabled = !includeVariations;
                  const varDate = v.variation_date
                    ? new Date(v.variation_date)
                    : null;
                  const isThisMonth =
                    varDate &&
                    varDate.getMonth() + 1 === selectedMonth &&
                    varDate.getFullYear() === selectedYear;

                  const waitingAmount = calculateVariationClaimContribution(
                    v,
                    previewHireSourceVariationIds,
                  );
                  const hasWaiting = Math.abs(waitingAmount) >= 0.01;

                  return (
                    <label
                      key={v.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.5 : 1,
                        background: isThisMonth
                          ? "rgba(22, 163, 74, 0.08)"
                          : hasWaiting
                            ? "rgba(217, 119, 6, 0.06)"
                            : "transparent",
                        borderBottom: "1px solid var(--border-color-light)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => {
                          const newSet = new Set(selectedVariationIds);
                          if (isSelected) {
                            newSet.delete(v.id);
                          } else {
                            newSet.add(v.id);
                          }
                          setSelectedVariationIds(newSet);
                        }}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "var(--text-primary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {(() => {
                            const raw = v.docket_no || "";
                            if (!raw) return "No Docket";
                            const lastNum = raw.match(/-(\\d+)$/)?.[1];
                            return lastNum ? `Docket: SEQ-${lastNum}` : raw;
                          })()}
                        </div>
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          {varDate
                            ? varDate.toLocaleDateString("en-AU", {
                              month: "short",
                              year: "numeric",
                            })
                            : "No date"}
                          {v.total
                            ? ` • $${Number(v.total).toLocaleString()}`
                            : ""}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isThisMonth && (
                          <span
                            style={{
                              fontSize: "0.65rem",
                              background: "#16a34a",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontWeight: 600,
                            }}
                          >
                            THIS MONTH
                          </span>
                        )}
                        {hasWaiting && (
                          <span
                            style={{
                              fontSize: "0.65rem",
                              background: "#d97706",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontWeight: 600,
                            }}
                          >
                            WAITING {waitingAmount < 0 ? "-$" : "$"}
                            {Math.abs(waitingAmount).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div
            style={{ height: "1px", background: "var(--border-color-light)" }}
          ></div>

          {/* Section 3: Uploads */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <h3
              style={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Upload size={16} color="var(--color-warning)" />
              Attachments
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
              }}
            >
              {/* General Upload */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label className="form-label">Supporting Docs</label>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "128px",
                    border: "2px dashed var(--border-color)",
                    borderRadius: "var(--border-radius-lg)",
                    background: "var(--bg-tertiary)",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      background: "white",
                      borderRadius: "50%",
                      marginBottom: "8px",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    <Upload size={20} color="var(--text-muted)" />
                  </div>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                    }}
                  >
                    Choose Files
                  </span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    style={{ display: "none" }}
                    accept=".pdf,.png,.jpg"
                    onChange={(e) => handleFileUpload(e, false)}
                  />
                </label>
                {uploadedFiles.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {uploadedFiles.map((f, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px",
                          background: "var(--bg-tertiary)",
                          borderRadius: "var(--border-radius-sm)",
                          fontSize: "0.75rem",
                        }}
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "120px",
                          }}
                        >
                          {f.name}
                        </span>
                        <button
                          onClick={() => removeFile(i)}
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: "var(--color-error)",
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Statutory Dec */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label className="form-label">Statutory Declaration</label>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "128px",
                    border: statutoryFile
                      ? "2px dashed var(--color-success)"
                      : "2px dashed var(--border-color)",
                    borderRadius: "var(--border-radius-lg)",
                    background: statutoryFile
                      ? "rgba(16, 185, 129, 0.05)"
                      : "var(--bg-tertiary)",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                >
                  {statutoryFile ? (
                    <>
                      <div
                        style={{
                          padding: "8px",
                          background: "rgba(16, 185, 129, 0.1)",
                          borderRadius: "50%",
                          marginBottom: "8px",
                        }}
                      >
                        <Check size={20} color="var(--color-success)" />
                      </div>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "var(--color-success)",
                          maxWidth: "140px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {statutoryFile.name}
                      </span>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          padding: "8px",
                          background: "white",
                          borderRadius: "50%",
                          marginBottom: "8px",
                          boxShadow: "var(--shadow-sm)",
                        }}
                      >
                        <FileText size={20} color="var(--text-muted)" />
                      </div>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "var(--text-secondary)",
                        }}
                      >
                        Upload Signed Dec
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    style={{ display: "none" }}
                    accept=".pdf,.png,.jpg"
                    onChange={(e) => handleFileUpload(e, true)}
                  />
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="form-label">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="form-input"
              style={{ height: "96px", resize: "none" }}
              placeholder="Add any additional notes for this claim..."
            />
          </div>
        </div>
      )}

      {/* Override Confirmation Modal */}
      {showOverrideConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowOverrideConfirm(false)}
        >
          <div
            className="card"
            style={{
              padding: "24px",
              maxWidth: "400px",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "rgba(251, 191, 36, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <AlertTriangle size={24} color="#f59e0b" />
            </div>
            <h3
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                marginBottom: "8px",
              }}
            >
              Report Already Exists
            </h3>
            <p
              style={{
                fontSize: "0.875rem",
                color: "var(--text-muted)",
                marginBottom: "20px",
              }}
            >
              A progress claim report for this month already exists. Generating
              a new one will override the existing report and PDF.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowOverrideConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={() => {
                  setShowOverrideConfirm(false);
                  handleGenerate(false, true);
                }}
              >
                Override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statutory Declaration Warning Modal */}
      {showStatutoryWarning && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowStatutoryWarning(false)}
        >
          <div
            className="card"
            style={{
              padding: "24px",
              maxWidth: "400px",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "rgba(251, 191, 36, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <span style={{ fontSize: "24px" }}>⚠️</span>
            </div>
            <h3
              style={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "var(--text-primary)",
                marginBottom: "8px",
              }}
            >
              No Statutory Declaration
            </h3>
            <p
              style={{
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
                marginBottom: "24px",
              }}
            >
              You haven't attached a Statutory Declaration. Are you sure you
              want to generate the report without it?
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowStatutoryWarning(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={() => {
                  setShowStatutoryWarning(false);
                  handleGenerate(true, true);
                }}
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
